import { gql } from 'graphql-request';
import type { QueryFunctionContext } from '@tanstack/react-query';
import { request } from '@utilities/graphQLClient';
import { subYears, format, subDays } from 'date-fns';
import { DivPaymentDetailRawData } from './types';

export interface QueryResponse {
  errors?: Error[];
  Company: DivPaymentDetailRawData;
}

export type ParamsType = {
  id: string;
};

export type QueryKeyType = [string, string];

export const DIVIDENDS_TIMESERIES_QUERY_KEY = 'dividends-timeseries';

export const getDividendsTimeseriesDataKey = ({
  id,
}: ParamsType): QueryKeyType => [DIVIDENDS_TIMESERIES_QUERY_KEY, id];

const getQuery = () => gql`
  query getCompanyDividends(
    $id: String
    $startDate: DateTime!
    $endDate: DateTime!
  ) {
    Company(id: $id) {
      name
      dividends(dateFrom: $startDate, dateTo: $endDate) {
        exDate
        payDate
        recordDate
        divAmount
        currencyIsoCode
        dividendTypeName
        divFreqTypeName
        annualizedDividend
        annualizedYield
      }
    }
  }
`;

export const getPeriodFromNow = () => {
  const now = new Date();
  const PAST_TEN_YEAR = 10;
  const PAST_ONE_DAY = 1;

  const endDate = format(subDays(now, PAST_ONE_DAY), 'yyyy-MM-dd');
  const startDate = format(subYears(now, PAST_TEN_YEAR), 'yyyy-MM-dd');
  return { startDate, endDate };
};

export const queryFn = async ({
  queryKey: [_, id],
}: QueryFunctionContext<QueryKeyType>) => {
  const { startDate, endDate } = getPeriodFromNow();
  const query = getQuery();

  const response = await request<QueryResponse>(query, {
    id,
    startDate,
    endDate,
  });

  if (response.errors) {
    const message = response.errors.map((e: Error) => e.message).join(' - ');
    throw new Error(message);
  }

  const { Company } = response;
  return Company;
};
